/**
 * Resources & Bookings web client. Thin typed wrappers over the shared
 * `apiFetch` helper (cookie auth, ApiError on non-2xx).
 */
import { ApiError } from '@fc/core'
import type {
  AvailabilityResponse,
  BookingConflict,
  NewBookingInput,
  NewResourceInput,
  Paginated,
  Resource,
  ResourceBooking,
  UpdateResourceInput,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  AvailabilityResponse,
  BookingConflict,
  NewBookingInput,
  NewResourceInput,
  Resource,
  ResourceBooking,
  UpdateResourceInput,
} from '@fc/core'

// ---- Resources ----
export const listResources = async (): Promise<Resource[]> => {
  const data = await apiFetch<Paginated<Resource>>('/resources')
  return data.items
}

export const getResource = (id: string) => apiFetch<Resource>(`/resources/${id}`)

export const createResource = (input: NewResourceInput) =>
  apiFetch<Resource>('/resources', { method: 'POST', body: JSON.stringify(input) })

export const updateResource = (id: string, input: UpdateResourceInput) =>
  apiFetch<Resource>(`/resources/${id}`, { method: 'PATCH', body: JSON.stringify(input) })

export const deleteResource = (id: string) =>
  apiFetch<void>(`/resources/${id}`, { method: 'DELETE' })

// ---- Bookings ----
export const getAvailability = (id: string, from: string, to: string) => {
  const qs = new URLSearchParams({ from, to }).toString()
  return apiFetch<AvailabilityResponse>(`/resources/${id}/availability?${qs}`)
}

export const listBookings = (
  id: string,
  opts?: { from?: string; to?: string; includeCanceled?: boolean },
): Promise<ResourceBooking[]> => {
  const params = new URLSearchParams()
  if (opts?.from) params.set('from', opts.from)
  if (opts?.to) params.set('to', opts.to)
  if (opts?.includeCanceled) params.set('includeCanceled', 'true')
  const qs = params.toString()
  return apiFetch<ResourceBooking[]>(`/resources/${id}/bookings${qs ? `?${qs}` : ''}`)
}

/** Error thrown when a booking request overlaps existing reservations (HTTP 409). */
export class BookingConflictError extends Error {
  readonly conflicts: BookingConflict[]
  constructor(message: string, conflicts: BookingConflict[]) {
    super(message)
    this.name = 'BookingConflictError'
    this.conflicts = conflicts
  }
}

interface ConflictBody {
  error: { code: string; message: string; conflicts?: BookingConflict[] }
}

/**
 * Create a booking. On a 409 the API returns `{ error: { code, message,
 * conflicts } }`. The shared `apiFetch` helper would drop the `conflicts`
 * array (its ApiError only carries code/message/fields), so this one endpoint
 * uses a direct fetch to preserve the structured conflict list, surfaced as a
 * {@link BookingConflictError} for inline UI feedback. Other 2xx/error cases
 * fall back to the standard ApiError shape.
 */
export const createBooking = async (
  id: string,
  input: NewBookingInput,
): Promise<ResourceBooking> => {
  const res = await fetch(`/api/resources/${id}/bookings`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })

  if (res.status === 409) {
    let body: ConflictBody | undefined
    try {
      body = (await res.json()) as ConflictBody
    } catch {
      /* non-JSON */
    }
    throw new BookingConflictError(
      body?.error?.message ?? 'This time slot conflicts with an existing booking.',
      body?.error?.conflicts ?? [],
    )
  }

  if (!res.ok) {
    let body: { error?: { code: string; message: string } } | undefined
    try {
      body = (await res.json()) as { error?: { code: string; message: string } }
    } catch {
      /* non-JSON */
    }
    throw new ApiError(
      res.status,
      body?.error ?? { code: 'http_error', message: `Request failed (${res.status})` },
    )
  }

  return (await res.json()) as ResourceBooking
}

export const cancelBooking = (resourceId: string, bookingId: string) =>
  apiFetch<ResourceBooking>(`/resources/${resourceId}/bookings/${bookingId}/cancel`, {
    method: 'POST',
  })
